import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Sparkles, Loader2, X } from "lucide-react";
import api from "../lib/api";

/**
 * DetailDrawer — drawer latéral réutilisable.
 * Props:
 *  - open, onOpenChange
 *  - title, subtitle
 *  - details: optional pre-written object { intro, stats[], actions[], checklist[], kpis[], errors[], proTips[], examples[] }
 *  - aiContext: { topic, context, platform } pour fallback IA si pas de details manuels
 */
export function DetailDrawer({ open, onOpenChange, title, subtitle, details, aiContext }) {
  const [aiContent, setAiContent] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchAi = async () => {
    if (!aiContext) return;
    setLoading(true); setAiContent("");
    try {
      const r = await api.aiDeepDive(aiContext);
      setAiContent(r.content || "");
    } catch {
      setAiContent("Erreur. Réessaie dans un instant.");
    } finally { setLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-[#0A0A0C] border-l border-[#1E1E24] text-white p-0 overflow-y-auto scrollbar-thin">
        <SheetHeader className="sticky top-0 z-10 bg-[#0A0A0C]/95 backdrop-blur-xl border-b border-[#1E1E24] px-6 py-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-1.5">// DEEP DIVE</div>
              <SheetTitle className="font-display text-2xl font-bold text-white tracking-tight leading-tight">{title}</SheetTitle>
              {subtitle && <p className="text-zinc-400 text-sm mt-2">{subtitle}</p>}
            </div>
          </div>
        </SheetHeader>

        <div className="px-6 py-6 space-y-6">
          {details ? <ManualDetails d={details} /> : null}

          {aiContext && (
            <div className="border border-[#00D4FF]/30 bg-[#00D4FF]/[0.03] rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#00D4FF]" strokeWidth={1.5} />
                  <span className="font-mono uppercase tracking-wider text-xs text-[#00D4FF]">Approfondir avec l'IA</span>
                </div>
                {!aiContent && !loading && (
                  <button onClick={fetchAi} data-testid="ai-deepdive-btn"
                    className="bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider">
                    Lancer
                  </button>
                )}
              </div>
              {loading && (
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <Loader2 className="w-4 h-4 animate-spin text-[#00D4FF]" /> Claude Sonnet 4.5 analyse...
                </div>
              )}
              {aiContent && (
                <div className="prose-content text-sm text-zinc-200 leading-relaxed whitespace-pre-line">{aiContent}</div>
              )}
              {!loading && !aiContent && !details && (
                <p className="text-sm text-zinc-500">Clique "Lancer" pour obtenir un deep-dive Claude Sonnet 4.5 ultra concret sur ce sujet : actions du jour, checklist, KPIs, erreurs, pro tips.</p>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ManualDetails({ d }) {
  return (
    <div className="space-y-5">
      {d.intro && (
        <p className="text-zinc-300 leading-relaxed border-l-2 border-[#CCFF00] pl-4">{d.intro}</p>
      )}

      {d.stats?.length > 0 && (
        <Block label="Stats clés" color="#CCFF00">
          <div className="grid grid-cols-2 gap-3">
            {d.stats.map((s, i) => (
              <div key={i} className="border border-[#1E1E24] bg-[#0C0C0E] rounded-sm p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{s.label}</div>
                <div className="font-mono text-lg text-white mt-1">{s.value}</div>
              </div>
            ))}
          </div>
        </Block>
      )}

      {d.actions?.length > 0 && (
        <Block label="Actions à faire AUJOURD'HUI" color="#00D4FF">
          <ol className="space-y-2">
            {d.actions.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-zinc-200 leading-relaxed">
                <span className="text-[#00D4FF] font-mono text-xs shrink-0 w-5">{String(i + 1).padStart(2, "0")}</span>
                <span>{a}</span>
              </li>
            ))}
          </ol>
        </Block>
      )}

      {d.checklist?.length > 0 && (
        <Block label="Checklist d'exécution" color="#CCFF00">
          <ul className="space-y-2">
            {d.checklist.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                <span className="w-4 h-4 border border-[#CCFF00]/40 bg-[#CCFF00]/5 rounded-sm shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {d.kpis?.length > 0 && (
        <Block label="KPIs à viser" color="#10B981">
          <div className="space-y-2">
            {d.kpis.map((k, i) => (
              <div key={i} className="flex items-center justify-between border-b border-[#1E1E24] pb-2 last:border-0">
                <span className="text-sm text-zinc-300">{k.label}</span>
                <span className="font-mono text-sm text-[#10B981]">{k.value}</span>
              </div>
            ))}
          </div>
        </Block>
      )}

      {d.errors?.length > 0 && (
        <Block label="Erreurs qui tuent ce levier" color="#EF4444">
          <ul className="space-y-2">
            {d.errors.map((e, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                <X className="w-4 h-4 text-[#EF4444] shrink-0 mt-0.5" strokeWidth={2} />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </Block>
      )}

      {d.proTips?.length > 0 && (
        <Block label="Pro tips avancés" color="#F59E0B">
          <ul className="space-y-2">
            {d.proTips.map((t, i) => (
              <li key={i} className="text-sm text-zinc-200 border-l-2 border-[#F59E0B]/40 pl-3 leading-relaxed">{t}</li>
            ))}
          </ul>
        </Block>
      )}

      {d.examples?.length > 0 && (
        <Block label="Exemples concrets" color="#A78BFA">
          <ul className="space-y-2">
            {d.examples.map((e, i) => (
              <li key={i} className="text-sm text-zinc-300 italic border border-[#1E1E24] rounded-sm p-3 bg-[#0C0C0E]">"{e}"</li>
            ))}
          </ul>
        </Block>
      )}
    </div>
  );
}

function Block({ label, color, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
        <h3 className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color }}>{label}</h3>
      </div>
      {children}
    </section>
  );
}
